// Recurring-parent surfacing rule (v3). Wraps deriveNextOccurrence
// with phase + reason classification so the parent-row renderer can
// pick the right copy ("Awaiting first occurrence" / "Next: …" /
// "All N complete"), italic state, and ring colour.
//
// Phase priority (top wins):
//   1. completed       — every child is terminal (accepted / closed).
//                        surfacedOccurrenceId = null; reason='all_complete'.
//   2. active.overdue  — any child derives to overdue (past-due,
//                        not-started, no submission). Surfaces the
//                        OLDEST overdue child (earliest dueDate).
//   3. active.next_due — at least one child is non-terminal AND at
//                        least one is terminal. Surfaces the
//                        chronologically NEXT non-terminal child.
//   4. awaiting_first  — no terminal children, no overdue. Surfaces
//                        the FIRST scheduled (earliest dueDate)
//                        non-terminal child.
//
// Tiebreaker for identical dueDates: id ascending (deterministic).
//
// finalRag is the worst-of-live-children effective RAG (red > amber
// > green; red if any live child derives overdue). Terminal children
// are ignored in the aggregation so an old red doesn't inflate the
// live signal.

import type { Deliverable, DeliverableStatus, RagRating } from "../../../../types";
import type { ParentRow } from "./groupDeliverables";
import { deriveDeliverableStatus } from "./deriveDeliverableStatus";
import { deriveEffectiveRag } from "./deriveEffectiveRag";

export type SurfacingPhase = "awaiting_first" | "active" | "completed";
export type SurfacingReason =
  | "first_pending"
  | "overdue"
  | "next_due"
  | "all_complete";

export interface SurfacingResult {
  phase: SurfacingPhase;
  surfacedOccurrenceId: string | null;
  surfacingReason: SurfacingReason;
  finalRag: RagRating | null;
  totalOccurrences: number;
}

const TERMINAL: ReadonlySet<DeliverableStatus> = new Set([
  "accepted",
  "closed",
]);

const RAG_PRIORITY: RagRating[] = ["red", "amber", "green"];

function isTerminal(d: Deliverable): boolean {
  return TERMINAL.has(d.status);
}

function isOverdue(d: Deliverable, today: Date): boolean {
  return (
    deriveDeliverableStatus(d.status, d.dueDate, today, d.submittedAt) ===
    "overdue"
  );
}

function sortKey(d: Deliverable): string {
  return d.dueDate ?? d.occurrenceDate ?? "￿";
}

function compareDeliverables(a: Deliverable, b: Deliverable): number {
  const sa = sortKey(a);
  const sb = sortKey(b);
  if (sa !== sb) return sa.localeCompare(sb);
  return a.id < b.id ? -1 : 1;
}

function computeFinalRag(
  children: readonly Deliverable[],
  today: Date,
): RagRating | null {
  const live = new Set<RagRating>();
  for (const c of children) {
    if (isTerminal(c)) continue;
    const effective = deriveEffectiveRag(c, today);
    if (effective) live.add(effective);
  }
  for (const r of RAG_PRIORITY) {
    if (live.has(r)) return r;
  }
  return null;
}

export function deriveSurfacing(
  parent: ParentRow,
  today: Date,
): SurfacingResult {
  const children = parent.children.map((c) => c.deliverable);
  const totalOccurrences = children.length;

  if (totalOccurrences === 0) {
    return {
      phase: "completed",
      surfacedOccurrenceId: null,
      surfacingReason: "all_complete",
      finalRag: null,
      totalOccurrences: 0,
    };
  }

  const finalRag = computeFinalRag(children, today);

  // 1. completed — every child terminal.
  if (children.every(isTerminal)) {
    return {
      phase: "completed",
      surfacedOccurrenceId: null,
      surfacingReason: "all_complete",
      finalRag,
      totalOccurrences,
    };
  }

  // 2. active.overdue — any child overdue. Surface oldest.
  const overdueChildren = children.filter((c) => isOverdue(c, today));
  if (overdueChildren.length > 0) {
    const sorted = [...overdueChildren].sort(compareDeliverables);
    return {
      phase: "active",
      surfacedOccurrenceId: sorted[0].id,
      surfacingReason: "overdue",
      finalRag,
      totalOccurrences,
    };
  }

  // No overdue and at least one non-terminal child.
  const nonTerminal = children.filter((c) => !isTerminal(c));
  const sortedNonTerminal = [...nonTerminal].sort(compareDeliverables);
  const surfaced = sortedNonTerminal[0];

  // 4. awaiting_first — every child is still `not-started` and none
  //    are overdue. "Awaiting first occurrence" copy applies because
  //    the supplier hasn't begun any cycle yet.
  if (children.every((c) => c.status === "not-started")) {
    return {
      phase: "awaiting_first",
      surfacedOccurrenceId: surfaced.id,
      surfacingReason: "first_pending",
      finalRag,
      totalOccurrences,
    };
  }

  // 3. active.next_due — work is in flight on at least one cycle.
  //    Surfaces the chronologically NEXT non-terminal child.
  return {
    phase: "active",
    surfacedOccurrenceId: surfaced.id,
    surfacingReason: "next_due",
    finalRag,
    totalOccurrences,
  };
}
